import React, { FC, useCallback, useEffect, useMemo, useState } from 'react'
import './CourseFilterTermSelect.scss'
import { ITerm, Terms } from 'app_types/admin/term/term.types'
import ReactSelectCommon from 'app_components/commons/reactSelect/containers/reactSelectCommon'
import TermService from 'app_services/admin/term/termService'

interface ICourseFilterTermSelectProps {
  label?: string
  name: string
  allowedTypes?: Terms[]
  isMulti?: boolean
  defaultValue?: string[]
  formHook: any
}

const CourseFilterTermSelect: FC<ICourseFilterTermSelectProps> = ({
  label,
  allowedTypes,
  isMulti = true,
  defaultValue,
  name,
  formHook,
}) => {
  const termService = useMemo(() => new TermService(), [])
  const [filterCategories, setFilterCategories] = useState<ITerm<{ typeRelated: Terms }>[]>([])
  const [terms, setTerms] = useState<ITerm[]>([])
  const nameCategoriesByRelatedType = useMemo<Record<Terms, string>>((): Record<Terms, string> => {
    if (!filterCategories?.length) return {} as Record<Terms, string>
    return filterCategories.reduce<Record<Terms, string>>((accum, term) => {
      accum = {
        ...accum,
        [term.custom?.typeRelated as Terms]: term.name
      }
      return accum
    }, {} as Record<Terms, string>)
  }, [filterCategories])
  const options = useMemo(() => {
    if (!terms?.length) return []
    return terms.map((term) => ({
      label: `${nameCategoriesByRelatedType[term.type]} - ${term.name}`,
      value: term._id
    }))
  }, [terms, nameCategoriesByRelatedType])
  const defaultValueFormatted = useMemo(() => {
    if (!defaultValue) return undefined
    return defaultValue.map((termId) => {
      const term = terms.find((term) => term._id === termId)
      if (!term) return null
      return {
        label: `${nameCategoriesByRelatedType[term.type]} - ${term.name}`,
        value: term._id
      }
    }).filter((option) => !!option)
  }, [defaultValue, terms, nameCategoriesByRelatedType])

  const fetchFilterCategories = useCallback(async () => {
    const response = await termService.list({ type: Terms.COURSE_FILTER_CATEGORY, enabled: true, nPerPage: '100' })
    if (response?.code === 200 && response?.terms?.length) {
      setFilterCategories(response.terms)
    }
  }, [termService])

  const fetchTerms = useCallback(async () => {
    if (!filterCategories?.length) return
    const relatedTypes = filterCategories?.map((term) => term.custom?.typeRelated) as Terms[]
    const types = allowedTypes ? [...relatedTypes].filter((type) => allowedTypes.includes(type)) : relatedTypes
    if (!types?.length) return
    const response = await termService.list({
      enabled: true,
      types,
      nPerPage: '1000',
    })
    if (response?.code === 200 && response?.terms) {
      setTerms(response.terms)
    }
  }, [allowedTypes, filterCategories, termService])

  useEffect(() => {
    fetchFilterCategories()
  }, [fetchFilterCategories])

  useEffect(() => {
    fetchTerms()
  }, [fetchTerms])

  return (
    <ReactSelectCommon
      isMulti={isMulti}
      options={options}
      identifier={name}
      label={label}
      formHooks={formHook}
      defaultValue={defaultValueFormatted}
      key={JSON.stringify(defaultValueFormatted)}
    />
  )
}

export default CourseFilterTermSelect
